import React, { useEffect, useState } from "react";
import {
  Form,
  Loading,
  ButtonSet,
  Tabs,
  Tab,
  Button,
  TextInput,
  TextArea,
  Row,
  Column,
  FormGroup,
  FileUploader,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  InlineNotification,
} from "carbon-components-react";
import {
  ArrowRight20,
  ArrowLeft20,
  TrashCan32,
  Edit24,
  SendAlt24,
  Undo24,
} from "@carbon/icons-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import Card from "../../components/Card/Card";
import SubmitButton from "../../components/Buttons/SubmitButton";
import CancelButton from "../../components/Buttons/CancelButton";
import http from "../../http-common";
import { prototypeSchema } from "../../services/SchemaService";
import { getDocumentTypeDescription } from "../../services/Utils";

const PROTOTYPE_DOC_TYPES = [
  {
    type: "USER_MANUAL",
    label: "Manual do Utilizador",
    description:
      "Carregue o manual do utilizador da plataforma. (formato PDF, máx. 10MB)",
    index: "1",
  },
  {
    type: "TECHNICAL_DOCUMENTATION",
    label: "Documentação Técnica",
    description:
      "Carregue a documentação técnica do sistema. (formato PDF, máx. 10MB)",
    index: "2",
  },
  {
    type: "PRESENTATION",
    label: "Apresentação",
    description:
      "Carregue a apresentação do protótipo. (formato PDF, máx. 10MB)",
    index: "3",
  },
  {
    type: "COMPLEMENTARY_MATERIAL",
    label: "Material Complementar",
    description:
      "Carregue materiais complementares relevantes. (formato PDF, máx. 10MB)",
    index: "4",
  },
];

const APPLICATION_SUBMITTED_STATUS_CODE = 2;

// Deadline: update this date to control when submissions close.
// Mozambique is CAT (UTC+2), so midnight CAT = 22:00 UTC the previous day.
// Example: 2026-03-31 24:00 CAT  →  2026-03-31T22:00:00Z
const SUBMISSION_DEADLINE = new Date("2026-03-27T22:00:00Z");

const CreatePrototypePage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [prototype, setPrototype] = useState(null);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [newCredential, setNewCredential] = useState({
    description: "",
    credentialUsername: "",
    credentialPassword: "",
  });
  const [credentialErrors, setCredentialErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitLocking, setIsSubmitLocking] = useState(false);
  const [fileStatus, setFileStatus] = useState({
    USER_MANUAL: "edit",
    TECHNICAL_DOCUMENTATION: "edit",
    PRESENTATION: "edit",
    COMPLEMENTARY_MATERIAL: "edit",
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(prototypeSchema),
    mode: "onBlur",
  });

  const isSubmitted = prototype?.prototypeStatus === "SUBMITTED";
  const isDraft = prototype?.prototypeStatus === "DRAFT";
  const isPastDeadline = new Date() > SUBMISSION_DEADLINE;
  // Only the deadline locks editing. Submitted prototypes remain editable until then.
  const isReadOnly = isPastDeadline;

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    await Promise.all([loadApplication(), loadMyPrototype()]);
  };

  const loadApplication = async () => {
    try {
      const response = await http.get("/applications/load-user-application");
      setApplication(response.data);
    } catch {
      // no application found for this user
    }
  };

  const loadMyPrototype = async () => {
    try {
      const response = await http.get("/prototypes/my");
      const data = response.data;
      setPrototype(data);
      reset({
        platformUrl: data.platformUrl,
        functionalityDescription: data.functionalityDescription,
        additionalInfo: data.additionalInfo,
      });
      if (data.credentials && data.credentials.length > 0) {
        setCredentials(Array.from(data.credentials));
      }
      if (data.documents && data.documents.length > 0) {
        const formattedDocs = data.documents.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          fileType: doc.fileType,
          documentType: doc.documentType,
          size: doc.size,
          existingFile: true,
        }));
        setDocuments(formattedDocs);
        const newFileStatus = { ...fileStatus };
        formattedDocs.forEach((doc) => {
          newFileStatus[doc.documentType] = "complete";
        });
        setFileStatus(newFileStatus);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 404 && status !== 400) {
        toast.error("Erro ao carregar protótipo");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result.split(",")[1]);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (documentType) => async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo excede o limite de 10MB");
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      return;
    }

    if (!file.type.includes("pdf")) {
      toast.error("Apenas arquivos PDF são permitidos");
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      return;
    }

    try {
      const base64Content = await fileToBase64(file);
      const newDocument = {
        fileName: file.name,
        fileContent: base64Content,
        fileType: file.type,
        documentType,
        size: file.size,
      };
      setDocuments((prev) => [
        ...prev.filter((doc) => doc.documentType !== documentType),
        newDocument,
      ]);
      setFileStatus((prev) => ({ ...prev, [documentType]: "complete" }));
      toast.success("Arquivo carregado com sucesso");
    } catch {
      toast.error("Erro ao processar arquivo");
    }
  };

  const handleRemoveDocument = async (documentType) => {
    const docToRemove = documents.find(
      (doc) => doc.documentType === documentType,
    );
    if (!docToRemove) return;

    try {
      if (docToRemove.id) {
        await http.delete(`/prototypes/documents/${docToRemove.id}`);
      }
      setDocuments((prev) =>
        prev.filter((doc) => doc.documentType !== documentType),
      );
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      toast.success("Documento removido com sucesso");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao remover documento",
      );
    }
  };

  const getExistingDocument = (documentType) =>
    documents.find((doc) => doc.documentType === documentType);

  const handleAddCredential = () => {
    const errors = {};
    if (!newCredential.description.trim())
      errors.description = "Campo obrigatório";
    if (!newCredential.credentialUsername.trim())
      errors.credentialUsername = "Campo obrigatório";
    if (!newCredential.credentialPassword.trim())
      errors.credentialPassword = "Campo obrigatório";
    if (Object.keys(errors).length > 0) {
      setCredentialErrors(errors);
      return;
    }
    if (credentials.length >= 10) {
      toast.error("Máximo de 10 credenciais atingido");
      return;
    }
    setCredentials((prev) => [...prev, { ...newCredential }]);
    setNewCredential({
      description: "",
      credentialUsername: "",
      credentialPassword: "",
    });
    setCredentialErrors({});
  };

  const handleRemoveCredential = async (credential, index) => {
    try {
      if (credential.id) {
        await http.delete(`/prototypes/credentials/${credential.id}`);
      }
      setCredentials((prev) => prev.filter((_, i) => i !== index));
      toast.success("Credencial removida com sucesso");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao remover credencial",
      );
    }
  };

  const onSave = async (data) => {
    try {
      setIsSubmitting(true);
      const newDocuments = documents.filter((doc) => !doc.existingFile);

      if (!prototype) {
        const payload = {
          ...data,
          applicationUuid: application?.uuid,
          documents: newDocuments,
          credentials,
        };
        await http.post("/prototypes", payload);
        toast.success("Protótipo guardado com sucesso");
      } else {
        const payload = {
          ...data,
          uuid: prototype.uuid,
          documents: newDocuments,
          credentials,
        };
        await http.put("/prototypes", payload);
        toast.success("Protótipo actualizado com sucesso");
      }
      await loadMyPrototype();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao guardar protótipo",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitPrototype = async () => {
    if (
      !window.confirm(
        "Tem a certeza que deseja submeter o protótipo? Esta acção é irreversível.",
      )
    ) {
      return;
    }
    try {
      setIsSubmitLocking(true);
      await http.put("/prototypes/submit", { uuid: prototype.uuid });
      toast.success("Protótipo submetido com sucesso");
      await loadMyPrototype();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao submeter protótipo",
      );
    } finally {
      setIsSubmitLocking(false);
    }
  };

  const nextTab = () => setActiveTab((prev) => prev + 1);
  const previousTab = () => setActiveTab((prev) => prev - 1);

  const isApplicationSubmitted =
    application?.currentStatusCode === APPLICATION_SUBMITTED_STATUS_CODE;
  const isFinalist = application?.isFinalist === true;

  if (!isLoading && !isApplicationSubmitted) {
    return (
      <Card title="O Meu Protótipo">
        <InlineNotification
          kind="warning"
          title="Candidatura não submetida"
          subtitle="Apenas concorrentes com candidatura submetida podem registar ou editar um protótipo. Submeta a sua candidatura primeiro."
          hideCloseButton
          style={{ maxWidth: "100%" }}
        />
      </Card>
    );
  }

  if (!isLoading && !isFinalist) {
    return (
      <Card title="O Meu Protótipo">
        <InlineNotification
          kind="warning"
          title="Não é finalista"
          subtitle="Apenas concorrentes classificados como finalistas podem registar ou submeter um protótipo."
          hideCloseButton
          style={{ maxWidth: "100%" }}
        />
      </Card>
    );
  }

  return (
    <Card title="O Meu Protótipo">
      <Loading
        active={isLoading || isSubmitting || isSubmitLocking}
        description="Active loading indicator"
        withOverlay={true}
      />

      {isPastDeadline && (
        <InlineNotification
          kind="error"
          title="Prazo encerrado"
          subtitle="O prazo para submissão de protótipos terminou. Não é possível editar ou submeter o protótipo."
          hideCloseButton
          style={{ marginBottom: "1rem", maxWidth: "100%" }}
        />
      )}

      {isSubmitted && !isPastDeadline && (
        <InlineNotification
          kind="success"
          title="Protótipo submetido"
          subtitle="O seu protótipo foi submetido com sucesso. Pode ainda editar e guardar alterações até ao encerramento do prazo."
          hideCloseButton
          style={{ marginBottom: "1rem", maxWidth: "100%" }}
        />
      )}

      <Form onSubmit={handleSubmit(onSave)}>
        <Tabs
          scrollIntoView={false}
          selected={activeTab}
          type="container"
          onSelectionChange={(e) => setActiveTab(e)}
        >
          <Tab href="#" id="tab-proto-0" label="Informação Geral">
            <FormGroup
              legendText="DADOS DO PROTÓTIPO"
              className="section-header"
            >
              <fieldset className="bx--fieldset">
                <Row className="mb-4">
                  <Column sm={12} md={12} lg={12}>
                    <TextInput
                      id="platformUrl"
                      labelText="Link de Acesso à Plataforma / Aplicativo *"
                      placeholder="https://..."
                      invalid={!!errors.platformUrl}
                      invalidText={errors.platformUrl?.message}
                      readOnly={isReadOnly}
                      {...register("platformUrl")}
                    />
                  </Column>
                </Row>

                <Row className="mb-4">
                  <Column sm={12} md={12} lg={12}>
                    <TextArea
                      id="functionalityDescription"
                      labelText="Descrição das Principais Funcionalidades *"
                      placeholder="Descreva as principais funcionalidades do aplicativo..."
                      rows={5}
                      invalid={!!errors.functionalityDescription}
                      invalidText={errors.functionalityDescription?.message}
                      readOnly={isReadOnly}
                      {...register("functionalityDescription")}
                    />
                  </Column>
                </Row>

                <Row>
                  <Column sm={12} md={12} lg={12}>
                    <TextArea
                      id="additionalInfo"
                      labelText="Informações Adicionais"
                      placeholder="Informações adicionais relevantes para avaliação técnica, operacional e estratégica..."
                      rows={4}
                      readOnly={isReadOnly}
                      {...register("additionalInfo")}
                    />
                  </Column>
                </Row>
              </fieldset>
            </FormGroup>

            <FormGroup
              legendText="CREDENCIAIS DE ACESSO"
              className="section-header"
            >
              <fieldset className="bx--fieldset">
                <p className="bx--label" style={{ marginBottom: "1rem" }}>
                  Forneça até 10 credenciais para que os avaliadores possam
                  aceder à plataforma. ({credentials.length}/10)
                </p>

                {!isReadOnly && credentials.length < 10 && (
                  <Row className="mb-3">
                    <Column sm={12} md={4} lg={4}>
                      <TextInput
                        id="newCredDescription"
                        labelText="Descrição *"
                        placeholder="Ex: Admin, Utilizador Regular, Convidado"
                        value={newCredential.description}
                        invalid={!!credentialErrors.description}
                        invalidText={credentialErrors.description}
                        onChange={(e) => {
                          setNewCredential((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }));
                          setCredentialErrors((prev) => ({
                            ...prev,
                            description: undefined,
                          }));
                        }}
                      />
                    </Column>
                    <Column sm={12} md={3} lg={3}>
                      <TextInput
                        id="newCredUsername"
                        labelText="Utilizador *"
                        placeholder="Nome de utilizador / email"
                        value={newCredential.credentialUsername}
                        invalid={!!credentialErrors.credentialUsername}
                        invalidText={credentialErrors.credentialUsername}
                        onChange={(e) => {
                          setNewCredential((prev) => ({
                            ...prev,
                            credentialUsername: e.target.value,
                          }));
                          setCredentialErrors((prev) => ({
                            ...prev,
                            credentialUsername: undefined,
                          }));
                        }}
                      />
                    </Column>
                    <Column sm={12} md={3} lg={3}>
                      <TextInput
                        id="newCredPassword"
                        labelText="Senha *"
                        placeholder="Senha de acesso"
                        value={newCredential.credentialPassword}
                        invalid={!!credentialErrors.credentialPassword}
                        invalidText={credentialErrors.credentialPassword}
                        onChange={(e) => {
                          setNewCredential((prev) => ({
                            ...prev,
                            credentialPassword: e.target.value,
                          }));
                          setCredentialErrors((prev) => ({
                            ...prev,
                            credentialPassword: undefined,
                          }));
                        }}
                      />
                    </Column>
                    <Column sm={12} md={2} lg={2}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          height: "100%",
                          paddingBottom: "0.125rem",
                        }}
                      >
                        <Button
                          kind="primary"
                          size="field"
                          type="button"
                          onClick={handleAddCredential}
                        >
                          Adicionar
                        </Button>
                      </div>
                    </Column>
                  </Row>
                )}

                <div style={{ marginTop: "1rem" }}>
                  {credentials.length > 0 ? (
                    <DataTable
                      className="mt-3"
                      rows={credentials.map((cred, index) => ({
                        id: String(cred.id || `new-${index}`),
                      }))}
                      headers={[
                        { key: "description", header: "Descrição" },
                        { key: "credentialUsername", header: "Utilizador" },
                        { key: "credentialPassword", header: "Senha" },
                        { key: "actions", header: "Ações" },
                      ]}
                    >
                      {({ headers, getHeaderProps, getTableProps }) => (
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map((header) => (
                                <TableHeader
                                  {...getHeaderProps({ header })}
                                  key={header.key}
                                >
                                  {header.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {credentials.map((cred, index) => (
                              <TableRow key={cred.id || `new-${index}`}>
                                <TableCell>{cred.description}</TableCell>
                                <TableCell>{cred.credentialUsername}</TableCell>
                                <TableCell>{cred.credentialPassword}</TableCell>
                                <TableCell>
                                  {!isReadOnly && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveCredential(cred, index)
                                      }
                                      className="bx--btn bx--btn--ghost bx--btn--sm"
                                    >
                                      <TrashCan32 />
                                    </button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </DataTable>
                  ) : (
                    <p style={{ color: "#6f6f6f", fontStyle: "italic" }}>
                      Nenhuma credencial adicionada.
                    </p>
                  )}
                </div>
              </fieldset>
            </FormGroup>

            <div className="tab-navigation">
              <p></p>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>

          <Tab href="#" id="tab-proto-1" label="Documentos">
            <FormGroup
              legendText="DOCUMENTOS DO PROTÓTIPO"
              className="section-header"
            >
              <fieldset className="bx--fieldset">
                {PROTOTYPE_DOC_TYPES.map(
                  ({ type, label, description, index }) => (
                    <Row key={type} className="mb-4">
                      <Column sm={12} md={12} lg={12}>
                        {isReadOnly ? (
                          <p>
                            <strong>
                              {index}. {label}:
                            </strong>{" "}
                            {getExistingDocument(type)?.fileName ||
                              "Não submetido"}
                          </p>
                        ) : (
                          <FileUploader
                            id={`uploader-${type}`}
                            labelTitle={`${index}. ${label}`}
                            labelDescription={description}
                            buttonLabel="Carregar Arquivo"
                            accept={[".pdf"]}
                            multiple={false}
                            filenameStatus={fileStatus[type]}
                            onChange={handleFileChange(type)}
                            onDelete={() => handleRemoveDocument(type)}
                            value={getExistingDocument(type)?.fileName || ""}
                          />
                        )}
                      </Column>
                    </Row>
                  ),
                )}

                {documents.length > 0 && (
                  <DataTable
                    rows={documents.map((doc) => ({
                      ...doc,
                      id: doc.documentType,
                    }))}
                    headers={[
                      { key: "fileName", header: "Nome do Ficheiro" },
                      { key: "documentType", header: "Tipo de Documento" },
                      { key: "actions", header: "Ações" },
                    ]}
                  >
                    {({ rows, headers, getHeaderProps, getTableProps }) => (
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map((header) => (
                              <TableHeader
                                {...getHeaderProps({ header })}
                                key={header.key}
                              >
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map((doc) => (
                            <TableRow key={doc.documentType}>
                              <TableCell>{doc.fileName}</TableCell>
                              <TableCell>
                                {getDocumentTypeDescription(doc.documentType)}
                              </TableCell>
                              <TableCell>
                                {!isReadOnly && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveDocument(doc.documentType)
                                    }
                                    className="bx--btn bx--btn--ghost bx--btn--sm"
                                  >
                                    <TrashCan32 />
                                  </button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </DataTable>
                )}
              </fieldset>
            </FormGroup>

            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
            </div>
          </Tab>
        </Tabs>

        {!isReadOnly && (
          <div className="form-buttons">
            <ButtonSet>
              <CancelButton icon={Undo24} label="Voltar" />
              <SubmitButton className="emprega-bg-green" icon={Edit24} />
            </ButtonSet>
            {isDraft && (
              <ButtonSet>
                <SubmitButton
                  type="button"
                  label="Submeter"
                  icon={SendAlt24}
                  onClick={onSubmitPrototype}
                />
              </ButtonSet>
            )}
          </div>
        )}
      </Form>
    </Card>
  );
};

export default CreatePrototypePage;
